import { Typography, Select, Avatar, Button } from 'antd';
import { useState, useEffect } from 'react';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useGetScheduledJobsByDateQuery } from '../scheduleQueries';
import { useSchedule } from '../ScheduleContext';
import { getInitials } from '../scheduleModel';
import dayjs, { Dayjs } from 'dayjs';
import JobsList from './JobsList';
import { ScheduleCreateEventFilters } from './scheduleCreateEventModel';

const { Text } = Typography;

type Props = {
  filters: ScheduleCreateEventFilters;
  onEventCreated?: () => void;
  selectedDate?: Dayjs;
};

const ScheduledJobsContent = ({ filters, onEventCreated, selectedDate: eventSelectedDate }: Props) => {
  const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [currentWeekStart, setCurrentWeekStart] = useState(dayjs().startOf('week'));
  const [selectedCrewMemberId, setSelectedCrewMemberId] = useState<number | null>(null);

  const { crew } = useSchedule();

  useEffect(() => {
    if (crew && crew.length > 0 && selectedCrewMemberId === null) {
      const crewWithEmail = crew.find(member => member.userEmail);
      if (crewWithEmail) {
        setSelectedCrewMemberId(crewWithEmail.id);
      }
    }
  }, [crew, selectedCrewMemberId]);

  const selectedCrewMember = crew?.find(member => member.id === selectedCrewMemberId);
  const crewLeadEmail = selectedCrewMember?.userEmail || '';

  const { data: scheduledJobs, isLoading } = useGetScheduledJobsByDateQuery(
    selectedDate,
    crewLeadEmail,
    !!crewLeadEmail
  );

  const getDayOfWeek = (date: string) => {
    return dayjs(date).format('ddd').toUpperCase();
  };

  const getDayOfMonth = (date: string) => {
    return dayjs(date).format('D');
  };

  const getWeekDates = () => {
    return Array.from({ length: 7 }, (_, i) => currentWeekStart.add(i, 'day'));
  };

  const handlePreviousWeek = () => {
    setCurrentWeekStart(currentWeekStart.subtract(1, 'week'));
  };

  const handleNextWeek = () => {
    setCurrentWeekStart(currentWeekStart.add(1, 'week'));
  };

  const handleToday = () => {
    const today = dayjs();
    setCurrentWeekStart(today.startOf('week'));
    setSelectedDate(today.format('YYYY-MM-DD'));
  };

  const weekDates = getWeekDates();

  return (
    <div style={{ padding: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <Button onClick={handleToday} type="default">Today</Button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Button
            icon={<LeftOutlined />}
            onClick={handlePreviousWeek}
            size="small"
            type="text"
          />
          <Button
            icon={<RightOutlined />}
            onClick={handleNextWeek}
            size="small"
            type="text"
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', justifyContent: 'center' }}>
        {weekDates.map((date) => {
          const dateStr = date.format('YYYY-MM-DD');
          const isSelected = dateStr === selectedDate;

          return (
            <div
              key={dateStr}
              onClick={() => setSelectedDate(dateStr)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
                minWidth: '44px'
              }}
            >
              <Text style={{ fontSize: '10px', fontWeight: 500, color: isSelected ? '#ff6b35' : '#8c8c8c', marginBottom: '4px' }}>
                {getDayOfWeek(dateStr)}
              </Text>
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  backgroundColor: isSelected ? '#ff6b35' : 'transparent',
                  transition: 'background-color 0.2s'
                }}
              >
                <Text style={{ fontSize: '18px', fontWeight: 600, color: isSelected ? 'white' : '#262626' }}>
                  {getDayOfMonth(dateStr)}
                </Text>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginBottom: '24px' }}>
        <Text style={{ fontSize: '14px', fontWeight: 600, display: 'block', marginBottom: '8px' }}>Crew Member</Text>
        <Select
          showSearch
          placeholder="Select crew member"
          optionFilterProp="label"
          style={{ width: '100%' }}
          onChange={(value) => setSelectedCrewMemberId(value)}
          value={selectedCrewMemberId}
        >
          {crew?.filter(member => member.userEmail).map((member) => (
            <Select.Option
              key={member.id}
              value={member.id}
              label={member.name}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Avatar
                  src={member.hasStockAvatar ? undefined : member.avatar}
                  size="small"
                  style={member.hasStockAvatar || !member.avatar ? { backgroundColor: member.color || '#d9d9d9', color: '#fff' } : undefined}
                >
                  {getInitials(member.name)}
                </Avatar>
                <span>{member.name}</span>
              </div>
            </Select.Option>
          ))}
        </Select>
      </div>

      <JobsList
        filters={{
          ...filters,
          crewMemberIds: selectedCrewMemberId ? [selectedCrewMemberId] : []
        }}
        scheduledJobs={scheduledJobs}
        isLoading={isLoading}
        onEventCreated={onEventCreated}
        selectedDate={eventSelectedDate}
      />
    </div>
  );
};

export default ScheduledJobsContent;
